import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  fetchPricingRules, 
  createPricingRule, 
  updatePricingRule, 
  deletePricingRule,
  togglePricingRule,
  importPricingRules,
  PricingRulesResponse 
} from '@/api/pricing-rules';
import { PricingRule } from '@/types/revenue-manager';

export function usePricingRules(propertyId: string) {
  return useQuery<PricingRulesResponse>({
    queryKey: ['pricing-rules', propertyId],
    queryFn: () => fetchPricingRules(propertyId),
    enabled: !!propertyId,
  });
}

export function useCreatePricingRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (rule: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'>) => 
      createPricingRule(propertyId, rule),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules', propertyId] });
    },
  });
}

export function useUpdatePricingRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ruleId, updates }: { ruleId: string; updates: Partial<PricingRule> }) => 
      updatePricingRule(propertyId, ruleId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules', propertyId] });
    },
  });
}

export function useDeletePricingRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ruleId: string) => deletePricingRule(propertyId, ruleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules', propertyId] });
    },
  });
}

export function useTogglePricingRule(propertyId: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ruleId, isActive }: { ruleId: string; isActive: boolean }) => 
      togglePricingRule(propertyId, ruleId, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pricing-rules', propertyId] });
    },
  });
}

export function useImportPricingRules() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ targetPropertyIds, rules }: { 
      targetPropertyIds: string[]; 
      rules: Omit<PricingRule, 'id' | 'createdAt' | 'updatedAt'>[] 
    }) => importPricingRules(targetPropertyIds, rules),
    onSuccess: (_, variables) => {
      // Invalidate all affected properties
      variables.targetPropertyIds.forEach(propertyId => {
        queryClient.invalidateQueries({ queryKey: ['pricing-rules', propertyId] });
      });
    },
  });
}
